import React from "react";
import { cn } from "@/lib/utils";

export function PageHeader({ title, subtitle, actions, breadcrumb, className }) {
  return (
    <div className={cn("mb-7", className)}>
      {breadcrumb && (
        <div className="text-[11px] uppercase tracking-widest text-[hsl(var(--text-subtle))] mb-2">{breadcrumb}</div>
      )}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-[26px] sm:text-[30px] font-bold tracking-tight leading-tight">{title}</h1>
          {subtitle && <p className="text-[14px] text-[hsl(var(--text-muted))] mt-1.5">{subtitle}</p>}
        </div>
        {actions && <div className="flex items-center gap-2">{actions}</div>}
      </div>
    </div>
  );
}

export function KPI({ label, value, sub, variant = "default" }) {
  const accent = {
    default: "",
    primary: "text-[hsl(var(--primary))]",
    success: "text-[hsl(var(--success))]",
    warning: "text-[hsl(var(--warning))]",
    danger: "text-[hsl(var(--danger))]",
  }[variant];
  return (
    <div className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--surface))] p-5 hover:shadow-[0_8px_24px_-12px_hsl(var(--primary)/0.25)] hover:-translate-y-[1px] transition-all">
      <div className="text-[10px] uppercase tracking-widest text-[hsl(var(--text-muted))] font-semibold">{label}</div>
      <div className={cn("mt-3 text-[26px] font-semibold tracking-tight font-mono", accent)}>{value}</div>
      {sub && <div className="text-[12px] text-[hsl(var(--text-muted))] mt-1">{sub}</div>}
    </div>
  );
}

export function Card({ children, className, title, right, ...rest }) {
  return (
    <div className={cn("rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--surface))]", className)} {...rest}>
      {title && (
        <div className="px-5 py-4 border-b border-[hsl(var(--border))] flex items-center justify-between">
          <h3 className="text-[14px] font-semibold tracking-tight">{title}</h3>
          {right}
        </div>
      )}
      {children}
    </div>
  );
}

export function Button({ children, variant = "primary", size = "md", className, ...rest }) {
  const base = "inline-flex items-center justify-center gap-2 font-semibold rounded-lg transition-all active:scale-[0.98] disabled:opacity-60 disabled:cursor-not-allowed";
  const sizes = { sm: "h-8 px-3 text-[12px]", md: "h-10 px-4 text-[13px]", lg: "h-11 px-5 text-[14px]" }[size];
  const v = {
    primary: "bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary-hover))] text-white shadow-[0_8px_24px_-6px_hsl(var(--primary)/0.4)]",
    secondary: "bg-[hsl(var(--surface))] border border-[hsl(var(--border))] hover:border-[hsl(var(--border-strong))] text-[hsl(var(--text))]",
    ghost: "text-[hsl(var(--text-muted))] hover:text-[hsl(var(--text))] hover:bg-[hsl(var(--surface-hover))]",
    danger: "bg-[hsl(var(--danger-muted))] text-[hsl(var(--danger))] hover:bg-[hsl(var(--danger))] hover:text-white",
  }[variant];
  return <button className={cn(base, sizes, v, className)} {...rest}>{children}</button>;
}

export function Input(props) {
  const { className, ...rest } = props;
  return (
    <input
      className={cn(
        "w-full h-10 px-3 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--surface))] outline-none focus:border-[hsl(var(--primary))] focus:ring-2 focus:ring-[hsl(var(--primary)/0.15)] text-[13px] placeholder:text-[hsl(var(--text-subtle))]",
        className
      )}
      {...rest}
    />
  );
}

export function Select({ className, children, ...rest }) {
  return (
    <select
      className={cn(
        "w-full h-10 px-3 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--surface))] outline-none focus:border-[hsl(var(--primary))] focus:ring-2 focus:ring-[hsl(var(--primary)/0.15)] text-[13px]",
        className
      )}
      {...rest}
    >
      {children}
    </select>
  );
}

export function Textarea({ className, ...rest }) {
  return (
    <textarea
      className={cn(
        "w-full min-h-[80px] p-3 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--surface))] outline-none focus:border-[hsl(var(--primary))] focus:ring-2 focus:ring-[hsl(var(--primary)/0.15)] text-[13px]",
        className
      )}
      {...rest}
    />
  );
}

export function Label({ children, className }) {
  return <label className={cn("text-[12px] font-semibold text-[hsl(var(--text-muted))]", className)}>{children}</label>;
}

export function Empty({ title = "Nothing here yet", subtitle, action }) {
  return (
    <div className="flex flex-col items-center justify-center text-center py-16 px-6">
      <div className="w-14 h-14 rounded-2xl bg-[hsl(var(--primary-muted))] flex items-center justify-center mb-4">
        <div className="w-6 h-6 rounded-md bg-[hsl(var(--primary))]/30" />
      </div>
      <div className="text-[15px] font-semibold">{title}</div>
      {subtitle && <div className="text-[13px] text-[hsl(var(--text-muted))] mt-1 max-w-md">{subtitle}</div>}
      {action && <div className="mt-5">{action}</div>}
    </div>
  );
}
